/**
 * 极练 Rehab & Fit —— 周报真实数据查询聚合层（阶段 8）。
 *
 * 铁律与设计：
 * 1. 严格从 Dexie IndexedDB 真实查询训练会话、组记录、体重、饮食；
 * 2. 严禁任何硬编码假会话；
 * 3. 聚合后调用 @jilian/core 的 calculateWeeklySummary 纯函数。
 */

import {
  calculateWeeklySummary,
  type WeeklyDietItem,
  type WeeklySessionSummaryItem,
  type WeeklySummaryResult,
  type WeeklyWeightItem,
} from "@jilian/core";
import { db } from "../db/db";
import { EXERCISE_BY_ID } from "../data/exercises";

export interface LoadWeeklySummaryOptions {
  userId: string;
  referenceDate: string; // YYYY-MM-DD
  targetTrainingDaysPerWeek?: number;
  dailyCalorieTarget?: number;
  dailyProteinTargetGrams?: number;
}

export async function loadWeeklySummaryData(
  options: LoadWeeklySummaryOptions,
): Promise<WeeklySummaryResult> {
  const { userId, referenceDate } = options;
  const ref = referenceDate.slice(0, 10);

  // 计算 7 天窗口
  const d = new Date(`${ref}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() - 6);
  const startDate = d.toISOString().slice(0, 10);

  const [allSessions, allMeals, allMeasurements, decisions] = await Promise.all([
    db.sessions.where("userId").equals(userId).toArray(),
    db.meals.where("userId").equals(userId).toArray(),
    db.measurements.where("userId").equals(userId).toArray(),
    db.progressionDecisions.where("userId").equals(userId).toArray(),
  ]);

  // 1. 过滤 7 天窗口内的已完成或提前结束会话
  const weekSessions = allSessions.filter(
    (s) =>
      (s.status === "completed" || s.status === "ended_early") &&
      s.localDateAtStart >= startDate &&
      s.localDateAtStart <= ref,
  );

  const sessionIds = weekSessions.map((s) => s.id);
  const items = sessionIds.length ? await db.exerciseItems.where("sessionId").anyOf(sessionIds).toArray() : [];
  const itemIds = items.map((i) => i.id);
  const sets = itemIds.length ? await db.sets.where("workoutExerciseId").anyOf(itemIds).toArray() : [];

  const itemsBySession = new Map<string, typeof items>();
  for (const it of items) {
    const list = itemsBySession.get(it.sessionId) ?? [];
    list.push(it);
    itemsBySession.set(it.sessionId, list);
  }

  const setsByItem = new Map<string, typeof sets>();
  for (const s of sets) {
    const list = setsByItem.get(s.workoutExerciseId) ?? [];
    list.push(s);
    setsByItem.set(s.workoutExerciseId, list);
  }

  const sessionSummaryItems: WeeklySessionSummaryItem[] = [];

  for (const sess of weekSessions) {
    const sessItems = itemsBySession.get(sess.id) ?? [];
    let totalVolumeKg = 0;
    let completedSetsCount = 0;
    const muscleSet = new Set<string>();

    for (const it of sessItems) {
      const itSets = setsByItem.get(it.id) ?? [];
      const ex = EXERCISE_BY_ID.get(it.exerciseId);
      if (ex?.primaryMuscles) {
        for (const m of ex.primaryMuscles) muscleSet.add(m);
      }

      for (const s of itSets) {
        if (s.status !== "completed") continue;
        completedSetsCount++;
        if (s.weightUnitScope !== "assistance" && s.actualWeightKg != null && s.reps != null) {
          totalVolumeKg += s.actualWeightKg * s.reps;
        }
      }
    }

    let durationMinutes = 45;
    if (sess.startedAt && sess.finishedAt) {
      const diffMs = new Date(sess.finishedAt).getTime() - new Date(sess.startedAt).getTime();
      if (diffMs > 0) {
        durationMinutes = Math.max(10, Math.min(180, Math.round(diffMs / 60000)));
      }
    }

    // 统计本次训练中达成进阶（increase）的动作数量
    const sessDecisions = decisions.filter(
      (dec) => dec.sourceSessionId === sess.id && dec.decision === "increase",
    );

    sessionSummaryItems.push({
      id: sess.id,
      localDate: sess.localDateAtStart,
      status: sess.status,
      totalVolumeKg: Math.round(totalVolumeKg * 10) / 10,
      completedSetsCount,
      durationMinutes,
      muscleGroups: Array.from(muscleSet),
      progressedExercisesCount: sessDecisions.length,
    });
  }

  // 2. 过滤 7 天窗口内的体重数据
  const weightItems: WeeklyWeightItem[] = allMeasurements
    .filter((m) => m.date >= startDate && m.date <= ref && m.weightKg > 0)
    .map((m) => ({ localDate: m.date, weightKg: m.weightKg }));

  // 3. 过滤 7 天窗口内的饮食数据
  const dietItems: WeeklyDietItem[] = allMeals
    .filter((m) => {
      const dStr = m.consumedAt ? m.consumedAt.slice(0, 10) : "";
      return dStr >= startDate && dStr <= ref;
    })
    .map((m) => ({
      localDate: m.consumedAt.slice(0, 10),
      calories: m.finalCalories ?? 0,
      proteinGrams: m.finalProtein ?? 0,
    }));

  return calculateWeeklySummary({
    referenceDate: ref,
    targetTrainingDaysPerWeek: options.targetTrainingDaysPerWeek ?? 4,
    dailyCalorieTarget: options.dailyCalorieTarget ?? 2200,
    dailyProteinTargetGrams: options.dailyProteinTargetGrams ?? 140,
    sessions: sessionSummaryItems,
    weights: weightItems,
    diets: dietItems,
  });
}
